/**
 * Step-Up Challenge Registry with Anti-Replay Nonce Verification
 * Binds high-privilege operations to single-use, action-specific challenge nonces.
 */

import type { ScopedAction } from './execution-context';

export interface StepUpChallenge {
  readonly challengeId: string;
  readonly action: ScopedAction;
  readonly resourceType?: string;
  readonly resourceId?: string;
  readonly workspaceId?: string;
  readonly issuedAt: string;
  readonly expiresAt: string;
  consumed: boolean;
}

export class StepUpChallengeRegistry {
  private challenges = new Map<string, StepUpChallenge>();

  /**
   * Generates a single-use action-and-resource-bound challenge nonce.
   */
  createChallenge(
    action: ScopedAction,
    resource?: { type?: string; id?: string; workspaceId?: string },
    ttlMs: number = 300_000 // 5 minutes default
  ): StepUpChallenge {
    const challengeId = `chal-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const challenge: StepUpChallenge = {
      challengeId,
      action,
      resourceType: resource?.type,
      resourceId: resource?.id,
      workspaceId: resource?.workspaceId,
      issuedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + ttlMs).toISOString(),
      consumed: false,
    };
    this.challenges.set(challengeId, challenge);
    return challenge;
  }

  /**
   * Verifies that the challenge matches the action, resource, and has not expired or been consumed.
   * If valid, atomically marks the challenge as consumed (anti-replay).
   */
  verifyAndConsume(
    challengeId: string,
    action: ScopedAction,
    resource?: { type?: string; id?: string; workspaceId?: string }
  ): boolean {
    const challenge = this.challenges.get(challengeId);
    if (!challenge || challenge.consumed) {
      return false;
    }

    // Expiration check
    if (Date.now() > Date.parse(challenge.expiresAt)) {
      this.challenges.delete(challengeId);
      return false;
    }

    // Action match
    if (challenge.action !== action) {
      return false;
    }

    // Resource bindings match if specified in challenge
    if (challenge.resourceId && challenge.resourceId !== resource?.id) {
      return false;
    }
    if (challenge.workspaceId && challenge.workspaceId !== resource?.workspaceId) {
      return false;
    }

    // Single-use consumption
    challenge.consumed = true;
    return true;
  }

  /**
   * Removes expired challenges from memory.
   */
  purgeExpired(): void {
    const now = Date.now();
    for (const [id, challenge] of this.challenges.entries()) {
      if (now > Date.parse(challenge.expiresAt) || challenge.consumed) {
        this.challenges.delete(id);
      }
    }
  }
}

export const globalStepUpChallengeRegistry = new StepUpChallengeRegistry();
